import { usePatchBatchCandidates } from "@api";
import { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { ISettingValueMaster } from "@bbj-types/setting";
import type { ObjectId } from "@bbj-types/utils";
import { useEffect } from "react";
import { getEmailFormDefaultValues, useNotifyByEmailModalForm, useSubmitEmail } from "./emailModalContent/logic";
import { useEligibleCandidates } from "./logics/eligible";
import { getSmsFormDefaultValues, useNotifyBySmsModalForm, useSubmitSms } from "./smsModalContent/logic";
import type { INotifyByEmailModalForm, INotifyBySmsModalForm, TCandidateData } from "./types";

export const filterEmailTemplateType = <T extends { type: CommunicationTemplateType }>(
  template: T
): template is T & { type: CommunicationTemplateType.EMAIL } => {
  return template.type === CommunicationTemplateType.EMAIL;
};

export const filterSmsTemplateType = <T extends { type: CommunicationTemplateType }>(
  template: T
): template is T & { type: CommunicationTemplateType.SMS } => {
  return template.type === CommunicationTemplateType.SMS;
};

export const getValidCandidateSpelling = (candidatesNumber: number) =>
  candidatesNumber === 1 ? "candidat" : "candidats";

export const useSubmitCandidateCoachingStep = () => {
  const [{ loading }, updateCandidates] = usePatchBatchCandidates();
  const submitCandidateCoachingStep = async (candidateIds: ObjectId[], statusValue?: ISettingValueMaster["value"]) => {
    if (statusValue) {
      await updateCandidates({
        candidateIds,
        field: "coachingStep",
        value: statusValue,
      });
    }
  };
  return { submitCandidateCoachingStep, loading };
};

export const useNotifyCandidateModal = (
  candidates: readonly TCandidateData[],
  onSubmitEmail: (data: INotifyByEmailModalForm) => Promise<void>,
  onSubmitSms: (data: INotifyBySmsModalForm) => Promise<void>,
  submitLoading: boolean,
  closeModal: () => void,
  open: boolean,
  type: CommunicationTemplateType,
  onSuccessEmail?: (() => Promise<void> | void) | undefined,
  onSuccessSMS?: (() => Promise<void> | void) | undefined,
  onParticipantsModified?: ((modifiedParticipants: Set<string>) => void) | undefined
) => {
  const eligibleCandidates = useEligibleCandidates(type, candidates);
  const { submitEmail, updateEmailLoading } = useSubmitEmail(onSubmitEmail, onSuccessEmail);
  const { submitSms, updateSmsLoading } = useSubmitSms(onSubmitSms, onSuccessSMS);
  const emailForm = useNotifyByEmailModalForm(eligibleCandidates);
  const smsForm = useNotifyBySmsModalForm(eligibleCandidates);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (open) {
      if (type === CommunicationTemplateType.EMAIL) {
        emailForm.reset(getEmailFormDefaultValues(eligibleCandidates));
      } else {
        smsForm.reset(getSmsFormDefaultValues(eligibleCandidates));
      }
    }
  }, [open]);

  const selectedModalProps =
    type === CommunicationTemplateType.EMAIL
      ? {
          ...emailForm,
          handleSubmit: emailForm.handleSubmit(async (data) => {
            await submitEmail(data);
            if (onParticipantsModified) {
              onParticipantsModified(new Set(eligibleCandidates.map((candidate) => candidate._id)));
            }
            closeModal();
          }),
        }
      : {
          ...smsForm,
          handleSubmit: smsForm.handleSubmit(async (data) => {
            await submitSms(data);
            if (onParticipantsModified) {
              onParticipantsModified(new Set(eligibleCandidates.map((candidate) => candidate._id)));
            }
            closeModal();
          }),
        };
  return {
    ...selectedModalProps,
    eligibleCandidates,
    loading: updateEmailLoading || updateSmsLoading || submitLoading,
  };
};
